import { Grid2 as Grid } from "@mui/material"
import Button from "@mui/material/Button"
import { Field, Formik } from "formik"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import * as yup from "yup"
import { Supporto } from "../../types"
import { transformErrorForForm } from "../../utils/utils"
import { ACFondo, ACVocabolarioMulti } from "./autocompletes"
import { FormikSelectVocabolario, FormikTextField } from "./formsHelpers"

type SupportoFormProps = {
  fondoId?: string
  item?: Supporto
  save: (values: Supporto) => Promise<void>
  ref: React.RefObject<HTMLButtonElement>
}

export default function SupportoForm({ fondoId, item, save, ref }: SupportoFormProps) {
  const { t } = useTranslation()

  const schema = useMemo(
    () =>
      yup.object().shape({
        fondo: yup.number().required(),
        descr_contenintore: yup.string(),
      }),
    [t],
  )

  return (
    <Formik
      validationSchema={schema}
      initialValues={
        item || {
          fondo: fondoId ? parseInt(fondoId) : undefined,
          descr_contenintore: "",
          genere: [],
        }
      }
      enableReinitialize
      onSubmit={(values, formikBag) => {
        return save(values as Supporto).catch((e) => {
          const localError = transformErrorForForm(e)
          formikBag.setErrors(localError)
        })
      }}
    >
      {({ handleSubmit, values, setFieldValue, setFieldTouched, errors }) => {
        return (
          <form onSubmit={handleSubmit} noValidate>
            <Grid container spacing={1} sx={{ py: 2 }}>
              <Grid size={6}>
                <ACFondo
                  label={t("supporto.fondo")}
                  margin="dense"
                  variant="outlined"
                  fullWidth
                  value={values?.fondo as number}
                  onChange={(value) => {
                    setFieldValue("fondo", value)
                  }}
                  error={errors.fondo}
                />
              </Grid>

              <Grid size={6}>
                <Field
                  component={FormikTextField}
                  name="segnatura"
                  label={t("supporto.segnatura")}
                  helperText={t("supporto.segnatura_helper")}
                  margin="dense"
                  fullWidth
                  variant="filled"
                  disabled
                  slotProps={{
                    input: {
                      readOnly: true,
                    },
                  }}
                />
              </Grid>

              {/* <Grid size={6}>
                <Field
                  component={FormikDatePicker}
                  name="data_edizione"
                  label={t("supporto.data_edizione")}
                  required
                  margin="dense"
                  type="date"
                  fullWidth
                  variant="outlined"
                />
              </Grid> */}

              <Grid size={6}>
                <Field
                  component={FormikTextField}
                  type="number"
                  name="cronologia_da"
                  label={t("documento.cronologia_da")}
                  margin="dense"
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              <Grid size={6}>
                <Field
                  component={FormikTextField}
                  type="number"
                  name="cronologia_a"
                  label={t("documento.cronologia_a")}
                  margin="dense"
                  fullWidth
                  variant="outlined"
                />
              </Grid>

              <Grid size={12}>
                <Field
                  component={FormikTextField}
                  name="titolo_proprio"
                  label={t("supporto.titolo_proprio")}
                  required
                  margin="dense"
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={6}
                />
              </Grid>

              <Grid size={12}>
                <ACVocabolarioMulti
                  label={t("fondo.genere")}
                  vocabolario="fondo_genere"
                  margin="dense"
                  variant="outlined"
                  fullWidth
                  value={values.genere as number[]}
                  onChange={(value) => {
                    setFieldValue("genere", value)
                  }}
                  onBlur={() => {
                    setFieldTouched("genere", true)
                  }}
                />
              </Grid>

              <Grid size={12}>
                <Field
                  component={FormikTextField}
                  name="complemento_titolo"
                  label={t("supporto.complemento_titolo")}
                  required
                  margin="dense"
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={6}
                />
              </Grid>

              <Grid size={12}>
                <Field
                  component={FormikTextField}
                  name="fonte_titolo"
                  label={t("supporto.fonte_titolo")}
                  required
                  margin="dense"
                  fullWidth
                  variant="outlined"
                />
              </Grid>

              <Grid size={12}>
                <Field
                  component={FormikTextField}
                  name="variante"
                  label={t("supporto.variante")}
                  required
                  margin="dense"
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={6}
                />
              </Grid>

              <Grid size={12}>
                <Field
                  component={FormikTextField}
                  name="complemento_variante"
                  label={t("supporto.complemento_variante")}
                  required
                  margin="dense"
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={6}
                />
              </Grid>

              <Grid size={12}>
                <Field
                  component={FormikTextField}
                  name="fonte_variante"
                  label={t("supporto.fonte_variante")}
                  required
                  margin="dense"
                  fullWidth
                  variant="outlined"
                />
              </Grid>

              <Grid size={12}>
                <Field
                  component={FormikTextField}
                  name="note_titolo"
                  label={t("supporto.note_titolo")}
                  required
                  margin="dense"
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={6}
                />
              </Grid>

              <Grid size={4}>
                <Field
                  component={FormikSelectVocabolario}
                  vocabolario="elemento"
                  name="elemento"
                  label={t("supporto.elemento")}
                  required
                  margin="dense"
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={6}
                />
              </Grid>

              <Grid size={12}>
                <Field
                  component={FormikTextField}
                  name="descr_contenitore"
                  label={t("supporto.descr_contenitore")}
                  required
                  margin="dense"
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={6}
                />
              </Grid>
              <Grid size={12}>
                <Field
                  component={FormikTextField}
                  name="inv_provvisorio"
                  label={t("supporto.inv_provvisorio")}
                  required
                  margin="dense"
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={6}
                />
              </Grid>
            </Grid>

            <Button ref={ref} type="submit" color="primary" sx={{ display: "none" }}></Button>
          </form>
        )
      }}
    </Formik>
  )
}
